import { useForm } from "react-hook-form";
import { Stack, Button, Group, Text, Loader, Center } from "@mantine/core";
import { useMemo, useEffect } from "react";
import resolver from "@congrs/resolver";
import { web_createTicketValidationSchema } from "@congrs/schemas";
import TicketsForm from "../Registrations/Form/TicketsForm";
import {
  useGetRegistration,
  useUpdateRegistrationTickets,
} from "../../api/registrations";

export default function EditTicketsForm({
  registrationId,
  onClose,
  registrationStatus = "pending",
}) {
  const { data: registration, isPending: isLoadingRegistration } =
    useGetRegistration({
      registrationId,
    });

  const updateMutation = useUpdateRegistrationTickets();

  const canEdit =
    registrationStatus === "pending" || registrationStatus === "invoiced";
  const canChangeNumberOfTickets = registrationStatus === "pending";

  const customFieldDefinitions = useMemo(() => {
    if (!registration?.customFields) return [];
    return registration.customFields.filter(
      (field) => field.appliedTo === "ticket"
    );
  }, [registration]);

  const ticketTypes = useMemo(() => {
    return registration?.ticketTypes || [];
  }, [registration]);

  const maxQuantity = canChangeNumberOfTickets
    ? registration?.registrationSettings?.maxQuantity
    : registration?.tickets?.length;

  const schema = useMemo(() => {
    return web_createTicketValidationSchema(customFieldDefinitions);
  }, [customFieldDefinitions]);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      registrationId: registrationId,
      tickets: [],
    },
    mode: "onChange",
    resolver: resolver(schema, {
      allErrors: true,
      strictTypes: false,
    }),
  });

  useEffect(() => {
    if (!isLoadingRegistration && registration) {
      const transformedTickets = (registration.tickets || []).map((ticket) => ({
        id: ticket.id,
        firstName: ticket.firstName || "",
        lastName: ticket.lastName || "",
        email: ticket.email || "",
        ticketTypeId: ticket.ticketTypeId ? String(ticket.ticketTypeId) : null,
        additionalFields: ticket.additionalFields || {},
      }));

      reset({
        registrationId: registrationId,
        tickets: transformedTickets,
      });
    }
  }, [isLoadingRegistration, registration, registrationId, reset]);

  const onSubmit = (data) => {
    if (!canEdit) {
      return;
    }

    let ticketsToUpdate = data.tickets;

    if (registrationStatus === "invoiced") {
      ticketsToUpdate = data.tickets.filter((ticket) => ticket.id);

      if (ticketsToUpdate.length === 0) {
        return;
      }
    }

    const payload = {
      registrationId: registrationId,
      tickets: ticketsToUpdate.map((ticket) => {
        const cleanedAdditionalFields = {};
        if (ticket.additionalFields) {
          Object.keys(ticket.additionalFields).forEach((key) => {
            const value = ticket.additionalFields[key];
            if (value === "" || (Array.isArray(value) && value.length === 0)) {
              cleanedAdditionalFields[key] = null;
            } else {
              cleanedAdditionalFields[key] = value;
            }
          });
        }

        return {
          id: ticket.id || null,
          firstName: ticket.firstName,
          lastName: ticket.lastName,
          email: ticket.email,
          ticketTypeId: ticket.ticketTypeId
            ? parseInt(ticket.ticketTypeId)
            : null,
          additionalFields: cleanedAdditionalFields,
        };
      }),
    };

    updateMutation.mutate(payload, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  if (isLoadingRegistration) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!registration) {
    return (
      <Center py="xl">
        <Text c="red">Failed to load registration data</Text>
      </Center>
    );
  }

  if (!canEdit) {
    return (
      <Center py="xl">
        <Text c="yellow">
          Tickets cannot be edited. Only registrations with status "pending" or
          "invoiced" can modify tickets.
        </Text>
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      <TicketsForm
        key={`edit-tickets-${registrationId}`}
        control={control}
        ticketTypes={ticketTypes}
        ticketCustomFields={customFieldDefinitions}
        maxQuantity={maxQuantity}
        disableQuantityChange={!canChangeNumberOfTickets}
      />

      <Group justify="flex-end" mt="md">
        <Button
          variant="default"
          onClick={onClose}
          disabled={updateMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          loading={updateMutation.isPending}
        >
          Save Changes
        </Button>
      </Group>
    </Stack>
  );
}
